/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Normalize TOC mapping API responses to the shape expected by applyTocMappingToSyllabus.
 * @file normalizeTocMappingResponse.js
 * @author Satyaki Bose
 */

import CommonUtils from '../../../common/utils/CommonUtils';
import { TOC_MAPPING_RESPONSE_SOURCE } from '../common/Constants';
/**
 * Parse Berlin run-graph output (JSON string or object).
 * @param {string|Object} output
 * @returns {Object}
 */
function parseBerlinOutput(output) {
  if (output && typeof output === 'object') {
    return output;
  }
  if (typeof output !== 'string' || !output.trim()) {
    throw new Error('Missing Berlin TOC mapping output');
  }
  const parsed = CommonUtils.safeParseJsonArray(output);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Failed to parse Berlin TOC mapping output');
  }

  return parsed;
}

function normalizeBerlinChapter(chapter) {
  if (!chapter?.num) return null;
  const tocId = chapter.id || chapter.tocId;
  const tocTitle = chapter.mappedTitle || chapter.tocTitle || chapter.title;

  return {
    num: chapter.num,
    ...(tocId && { tocId }),
    ...(tocTitle && { tocTitle })
  };
}

function normalizeBerlinSection(section) {
  if (!section?.num) return null;
  const tocId = section.id || section.tocId;

  return {
    num: String(section.num),
    ...(tocId && { tocId })
  };
}

function normalizeFlatSections(mappingResponse) {
  const sections = [];
  const seenTocIds = new Set();

  const addSectionFromResponse = (section) => {
    const mappedSection = normalizeBerlinSection(section);
    if (!mappedSection?.tocId || seenTocIds.has(mappedSection.tocId)) return;
    seenTocIds.add(mappedSection.tocId);
    sections.push(mappedSection);
  };

  (mappingResponse?.sections || []).forEach(addSectionFromResponse);

  return sections;
}

function normalizeMappingPayload(parsed) {
  return {
    chapters: (parsed?.chapters || []).map(normalizeBerlinChapter).filter(Boolean),
    sections: normalizeFlatSections(parsed)
  };
}

/**
 * Normalize TOC mapping API responses to the shape expected by applyTocMappingToSyllabus.
 * @param {Object} response - Axios-style response
 * @param {string} source - TOC_MAPPING_RESPONSE_SOURCE.PRISM or .BERLIN
 * @returns {{ chapters: Array, sections: Array }}
 */
export default function normalizeTocMappingResponse(response, source = TOC_MAPPING_RESPONSE_SOURCE.PRISM) {
  if (source === TOC_MAPPING_RESPONSE_SOURCE.PRISM) {
    return normalizeMappingPayload(response?.data || {});
  }
  const { output } = response?.data || {};
  if (!output) {
    throw new Error('Missing Berlin TOC mapping output');
  }
  const parsed = parseBerlinOutput(output);

  return normalizeMappingPayload(parsed);
}
